<template>
  <Layout>
    <div class="row">
      <div class="col-lg-12">
        <div class="card border border-radius-12">
          <div class="card-body table mb-0">
            <div class="toolbar-container px-3">
              <div class="toolbar-row d-flex align-items-center justify-content-between mb-2">
                <PageHeader :title="title" :items="items" class="poppins fw-normal ps-0 text-nowrap" />
                <div class="d-flex gap-2 flex-wrap p-0 buttons-container">
                  <CustomButton 
                    :buttonText="downloadReportProcessing ? 'Please wait...' : 'Download Report'"
                    :loading="downloadReportProcessing"
                    class="p-0"
                    @click="downloadReport" 
                  />
                </div>
              </div>

              <div class="toolbar-row d-flex gap-2 align-items-center">
                <searchInput @getSearched="handleSearch" class="p-0" />
                <pageSize @getPageSize="handlePageSize" class="page-size-btn p-0"/> 
              </div>
            </div>

            <GeneralTable
              :isProcessing="loading"
              :items="invoiceItems"
              :fields="fields"
              @no-records-found="onRecordsFount"
              :showEyeButton="true"
              :showActionButton="false"
              @showItem="openInvoiceModal"
            />

            <!-- INVOICE MODAL -->
            <b-modal ref="invoiceModal" hide-footer title="Invoice Details" size="xl">
              <div v-if="modalLoading">
                <APILoader :loading="modalLoading" class="align-middle text-center" />
              </div>

              <div v-else-if="selectedInvoice" class="invoice-info">
                <!-- LEFT COLUMN -->
                <div class="invoice-column">
                  <p><strong>Invoice #:</strong> {{ selectedInvoice.number }}</p>
                  <p><strong>Status:</strong> {{ selectedInvoice.status }}</p>
                  <p><strong>Currency:</strong> {{ selectedInvoice.currency?.toUpperCase() }}</p>

                  <p><strong>Customer Name:</strong> {{ selectedInvoice.customer_name }}</p>
                  <p><strong>Email:</strong> {{ selectedInvoice.customer_email }}</p>
                  <p><strong>Receipt #:</strong> {{ selectedInvoice.receipt_number ?? '—' }}</p>

                  <p><strong>Billing Reason:</strong> {{ selectedInvoice.billing_reason }}</p>
                  <p><strong>Collection Method:</strong> {{ selectedInvoice.collection_method }}</p>
                  <p><strong>Paid:</strong> {{ selectedInvoice.paid ? 'Yes' : 'No' }}</p>
                </div>

                <!-- RIGHT COLUMN -->
                <div class="invoice-column">
                  <p><strong>Total:</strong> {{ formatAmount(selectedInvoice.total) }}</p>
                  <p><strong>Amount Paid:</strong> {{ formatAmount(selectedInvoice.amount_paid) }}</p>
                  <p><strong>Amount Remaining:</strong> {{ formatAmount(selectedInvoice.amount_remaining) }}</p>

                  <p><strong>Created At:</strong> {{ formatDate(selectedInvoice.created) }}</p>
                  <p>
                    <strong>Period:</strong>
                    {{ formatDate(selectedInvoice.period_start) }}
                    →
                    {{ formatDate(selectedInvoice.period_end) }}
                  </p>

                  <p><strong>Charge ID:</strong> {{ selectedInvoice.charge }}</p>
                  <p><strong>Payment Intent:</strong> {{ selectedInvoice.payment_intent }}</p>
                </div>

                <!-- LINE ITEMS -->
                <div class="invoice-column full-width">
                  <h6>Line Items</h6>

                  <div
                    v-for="line in selectedInvoice.lines?.data"
                    :key="line.id"
                    class="line-item"
                  >
                    <p class="mb-1"><strong>{{ line.description }}</strong></p>
                    <small>
                      {{ formatAmount(line.amount) }} × {{ line.quantity }}
                    </small>
                  </div>
                </div>

                <!-- LINKS -->
                <div class="invoice-column full-width">
                  <a
                    v-if="selectedInvoice.invoice_pdf"
                    :href="selectedInvoice.invoice_pdf"
                    target="_blank"
                  >
                    📄 View Invoice PDF
                  </a>
                  <br />
                  <a
                    v-if="selectedInvoice.hosted_invoice_url"
                    :href="selectedInvoice.hosted_invoice_url"
                    target="_blank"
                  >
                    🌐 Invoice on Stripe link 
                  </a>
                </div>
              </div>
            </b-modal>

            <Pagination
              @paginator="handlePaginationClick"
              :pagination="paginatelinks"
              :showing="paginate"
              @page-changed="handlePageChange"
            />
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../../layouts/static/main";
import PageHeader from "@/components/static/page-header";
import appConfig from "@/app.config";
import GeneralTable from "../../../../components/MagicTable/GeneralTable.vue";
import Pagination from "../../../../components/general/pagination.vue";
import searchInput from "../../../../components/general/searchInput.vue";
import pageSize from "../../../../components/general/pageSize.vue";
import CustomButton from "../../../../components/CustomButton.vue";
import APILoader from "@/components/general/APILoader.vue";
import clientSidePagination from "@/mixins/clientSidePagination";
import Swal from "sweetalert2";

export default {
  page: {
    title: "Invoices",
    meta: [
      {
        name: "invoices",
        content: appConfig.description,
      },
    ],
  },

  mixins: [clientSidePagination],

  components: {
    Layout,
    PageHeader,
    GeneralTable,
    Pagination,
    searchInput,
    pageSize,
    CustomButton,
    APILoader,
  },

  data() {
    return {
      title: "Invoices",
      items: [
        {
          text: "Omaha",
        },
        {
          text: "Invoices",
          active: true,
        },
      ],

      fields: [
        { key: "number", label: "Invoice #", tdClass: "align-middle" },
        { key: "customer_email", label: "Customer Email", tdClass: "align-middle" },
        { key: "status", label: "Status", tdClass: "align-middle" },
        { key: "total", label: "Total", tdClass: "align-middle" },
        { key: "created_at", label: "Created At", tdClass: "align-middle" },
        { key: "action", tdClass: "" },
      ],

      invoiceItems: [],
      selectedInvoice: null,
      loading: false,
      modalLoading: false,
      downloadReportProcessing: false,
    };
  },

  watch: {
    paginatedData: {
      handler(newData) {
        this.invoiceItems = newData.map((invoice) => ({
          id: invoice.id,
          number: invoice.number || "N/A",
          customer_email: invoice.customer_email || "N/A",
          status: invoice.status || "unknown",
          total: this.formatAmount(invoice.total),
          created_at: this.formatDate(invoice.created),
          _statusVariant: invoice.status === 'paid' ? 'success' : 'warning',
        }));
      },
      immediate: true,
    },
  },

  mounted() {
    this.getInvoices();
  },

  methods: {
    getFilteredData() {
      if (!this.search) {
        return this.allData;
      }

      const searchLower = this.search.toLowerCase();
      return this.allData.filter(invoice => {
        return (
          (invoice.customer_email || '').toLowerCase().includes(searchLower) ||
          (invoice.number || '').toLowerCase().includes(searchLower) ||
          (invoice.status || '').toLowerCase().includes(searchLower)
        );
      });
    },

    formatAmount(amount = 0) {
      if (amount == null) return "$0.00";
      return `$${(amount / 100).toFixed(2)}`;
    },

    formatDate(timestamp) {
      if (!timestamp) return "—";
      return new Date(timestamp * 1000).toLocaleString();
    },

    onRecordsFount() {
      this.resetPagination();
    },

    openInvoiceModal(invoiceId) {
      this.modalLoading = true;
      this.selectedInvoice = null;
      const invoice = this.allData.find((inv) => inv.id === invoiceId);
      
      if (invoice) {
        this.selectedInvoice = invoice;
        this.modalLoading = false;
        this.$refs.invoiceModal.show();
      }
    },

    getInvoices() {
      this.loading = true;

      let endPoint = "all-invoices";
      
      if (this.$route.query.currentPage) {
        endPoint = `all-invoices?page=${this.$route.query.currentPage}`;
      }

      this.$axios
        .get(endPoint, {
          params: {
            per_page: 1000,
          },
        })
        .then((response) => {
          setTimeout(() => this.handleInvoiceResponse(response), 300);
        })
        .catch((error) => {
          console.log(error?.response || error);
          this.loading = false;
        });
    },

    handleInvoiceResponse(response) {
      console.log('Invoice Response:', response);
      
      const payload = response?.data?.data ? response.data : response?.data || {};

      const isPaginated = payload.links && Array.isArray(payload.links);

      if (isPaginated) {
        // if backend provide pagination
        this.handleBackendPagination(payload);
      } else {
        // use client side pagination
        const allRows = Array.isArray(payload.data) ? payload.data : payload;
        this.initializePagination(allRows, {
          pageSize: this.pageSize,
          currentPage: this.currentPage,
        });
      }
      // console.log('Paginate Links:', this.paginatelinks);
      // console.log('Paginate Info:', this.paginate);
      
      this.loading = false;
    },

    handleBackendPagination(payload) {
      const rows = Array.isArray(payload?.data) ? payload.data : [];
    
      this.initializePagination(rows, {
        pageSize: this.pageSize,
        currentPage: payload.current_page || 1,
      });
    },

    async downloadReport() {
      if (this.downloadReportProcessing) return;

      this.downloadReportProcessing = true;

      try {
        const dataToDownload = this.getFilteredData();

        if (!dataToDownload.length) {
          Swal.fire("No data available to download.", "", "warning");
          return;
        }

        const headers = [
          "Invoice ID",
          "Invoice Number",
          "Customer Name",
          "Customer Email",
          "Status",
          "Currency",
          "Total",
          "Amount Paid",
          "Amount Remaining",
          "Created At",
          "Period Start",
          "Period End",
        ];

        const csvRows = dataToDownload.map((inv) => [
          inv.id,
          inv.number,
          inv.customer_name || "",
          inv.customer_email || "",
          inv.status,
          inv.currency?.toUpperCase?.() || "",
          (inv.total ?? 0) / 100,
          (inv.amount_paid ?? 0) / 100,
          (inv.amount_remaining ?? 0) / 100,
          this.formatDate(inv.created),
          this.formatDate(inv.period_start),
          this.formatDate(inv.period_end),
        ]);

        let csvContent = headers.join(",") + "\n";

        csvRows.forEach((row) => {
          csvContent +=
            row
              .map((value) =>
                `"${String(value ?? "")
                  .replace(/"/g, '""')
                  .replace(/\n/g, " ")}"`
              )
              .join(",") + "\n";
        });

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `invoices-report-${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error(error);
        Swal.fire("Something went wrong while downloading.", "error");
      } finally {
        this.downloadReportProcessing = false;
      }
    },
  },
};
</script>

<style scoped>
.invoice-info {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.invoice-column {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 12px;
}

.full-width {
  grid-column: span 2;
}

.line-item {
  border-bottom: 1px dashed #ccc;
  padding-bottom: 6px;
  margin-bottom: 6px;
}

@media (max-width: 768px) {
  .invoice-info {
    grid-template-columns: 1fr;
  }
  .full-width {
    grid-column: span 1;
  }
}

.toolbar-container {
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
}

.toolbar-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  width: 100%;
}

@media (max-width: 576px) {
  .card-body {
    padding: 0.25rem !important;
  }
  .toolbar-row {
    justify-content: flex-start;
  }

  .toolbar-row:first-child {
    flex-direction: column;
    align-items: flex-start;
  }
  .buttons-container {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .table > :not(caption) > * > * {
    padding: 0.25rem !important;
  }
}
</style>